// Blog articles — SEO-optimised French content for construction planning
export const ARTICLES = [
  {
    slug: "faire-un-planning-de-chantier",
    title: "Comment faire un planning de chantier étape par étape",
    description:
      "Guide pratique pour créer un planning de chantier fiable : découpage en corps d'état, estimation des durées, dépendances, chemin critique et outils.",
    keywords:
      "faire un planning de chantier, planning travaux, méthodologie chantier BTP, diagramme de Gantt chantier",
    publishedAt: "2026-02-15",
    readingMin: 9,
    heroEmoji: "📐",
    sections: [
      {
        type: "p",
        content:
          "Un planning de chantier bien construit est le premier levier de rentabilité d'une opération BTP. Il structure la coactivité, sécurise les délais contractuels et permet d'anticiper les dérives avant qu'elles ne coûtent cher. Voici la méthode complète, applicable à tout projet — de la rénovation d'un T2 à la construction d'un bâtiment tertiaire.",
      },
      { type: "h2", content: "1. Recenser les tâches par corps d'état" },
      {
        type: "p",
        content:
          "La première étape consiste à décomposer le chantier en tâches unitaires, regroupées par corps d'état (lot). Un projet de construction neuf comporte typiquement entre 60 et 150 tâches. Pour une rénovation, comptez plutôt 20 à 40 tâches. Chaque tâche doit être suffisamment fine pour être planifiée (durée < 15 jours ouvrés) mais assez grosse pour ne pas noyer la lecture.",
      },
      {
        type: "p",
        content:
          "Appuyez-vous systématiquement sur les devis des entreprises et le CCTP (cahier des clauses techniques particulières) : ils décrivent le périmètre exact des prestations à planifier.",
      },
      { type: "h2", content: "2. Estimer les durées de manière réaliste" },
      {
        type: "p",
        content:
          "Trois pièges classiques à éviter : sous-estimer les temps de séchage (dalles, chapes, enduits), oublier les délais d'approvisionnement (menuiseries sur mesure : 6 à 10 semaines), négliger les congés (juillet-août, fêtes de fin d'année).",
      },
      {
        type: "p",
        content:
          "Pour chaque tâche, demandez à l'entreprise concernée sa durée prévisionnelle. Ajoutez ensuite une marge de 10 à 15 % pour couvrir les aléas courants (intempéries, malfaçons à reprendre, absences).",
      },
      { type: "h2", content: "3. Définir les dépendances entre tâches" },
      {
        type: "p",
        content:
          "Une dépendance signifie qu'une tâche B ne peut démarrer qu'une fois la tâche A terminée. Il existe quatre types de liens :",
      },
      {
        type: "ul",
        items: [
          "Fin-Début (le plus courant) : B commence quand A finit",
          "Début-Début : A et B démarrent en même temps",
          "Fin-Fin : A et B doivent finir en même temps",
          "Début-Fin (rare) : B ne peut finir tant que A n'a pas démarré",
        ],
      },
      {
        type: "p",
        content:
          "Sur un chantier bâtiment, les dépendances techniques incontournables sont : terrassement → fondations → structure → charpente → couverture → menuiseries extérieures → plâtrerie → électricité/plomberie → revêtements → finitions.",
      },
      { type: "h2", content: "4. Identifier le chemin critique" },
      {
        type: "p",
        content:
          "Le chemin critique est la séquence de tâches dont le moindre retard décale la fin du chantier. Il représente en général 30 à 50 % des tâches totales. Marquer ces tâches (en rouge ou en couleur vive sur votre planning) permet à toute l'équipe de connaître les priorités.",
      },
      {
        type: "p",
        content:
          "Une tâche hors chemin critique dispose d'une marge : elle peut glisser sans impacter la livraison. Une tâche critique ne pardonne aucun décalage.",
      },
      { type: "h2", content: "5. Formaliser sur un diagramme de Gantt" },
      {
        type: "p",
        content:
          "Le diagramme de Gantt est la représentation visuelle universelle du planning. Chaque tâche est une barre horizontale, positionnée sur un axe temporel. La longueur de la barre = la durée. Les flèches entre barres = les dépendances.",
      },
      {
        type: "p",
        content:
          "Les outils historiques (MS Project, Primavera) coûtent cher et sont lourds à prendre en main. Des solutions en ligne comme ChantiPlanning génèrent votre Gantt automatiquement à partir de vos devis, en quelques secondes. C'est particulièrement adapté pour les petits chantiers et la rénovation.",
      },
      { type: "h2", content: "6. Diffuser, suivre et mettre à jour" },
      {
        type: "p",
        content:
          "Un planning n'a de valeur que s'il est utilisé. Diffusez-le à toutes les entreprises dès le lancement du chantier. Mettez-le à jour au minimum une fois par semaine (réunion de chantier hebdomadaire), en marquant l'avancement réel de chaque tâche.",
      },
      {
        type: "p",
        content:
          "Comparer le réalisé au prévu vous alerte immédiatement sur les dérives. En cas de retard sur une tâche critique, vous devez soit renforcer les moyens, soit accepter le glissement de la fin de chantier.",
      },
      { type: "h2", content: "Conclusion" },
      {
        type: "p",
        content:
          "Faire un planning de chantier ne demande pas des heures : la méthode ci-dessus s'applique en 2 à 4 heures pour un chantier moyen. L'important est la rigueur du découpage et le suivi hebdomadaire. Un outil intelligent réduit encore ce temps à quelques minutes, en générant automatiquement l'ossature à partir de vos devis.",
      },
    ],
  },
  {
    slug: "corps-d-etat-chantier-btp",
    title: "Les 8 corps d'état d'un chantier BTP : rôle, ordre et durées",
    description:
      "Panorama complet des 8 corps d'état d'un chantier bâtiment : mission, positionnement dans le planning, durées types et interfaces à surveiller.",
    keywords:
      "corps d'état, corps d'état chantier, corps d'état second œuvre, gros œuvre BTP, lots BTP",
    publishedAt: "2026-02-18",
    readingMin: 8,
    heroEmoji: "🏗️",
    sections: [
      {
        type: "p",
        content:
          "Comprendre les 8 corps d'état d'un chantier bâtiment est indispensable pour planifier correctement une opération de construction ou de rénovation. Chaque corps d'état a un rôle précis, un positionnement obligé dans le planning et des interfaces avec les autres lots.",
      },
      { type: "h2", content: "1. Gros Œuvre / Structure" },
      {
        type: "p",
        content:
          "Le gros œuvre porte le bâtiment. Il comprend le terrassement, les fondations, les murs porteurs, les planchers et la couverture provisoire. C'est la première phase d'un chantier neuf : sans elle, aucun autre lot ne peut intervenir.",
      },
      {
        type: "p",
        content:
          "Durée type : 20 à 40 % de la durée totale du chantier. Sur une maison individuelle de 120 m², comptez 3 à 4 mois. En rénovation lourde, le gros œuvre se limite souvent à quelques ouvertures et renforts (1 à 3 semaines).",
      },
      { type: "h2", content: "2. Charpente / Couverture" },
      {
        type: "p",
        content:
          "La charpente supporte la toiture ; la couverture (tuiles, zinc, ardoise) assure l'étanchéité. Ces deux lots interviennent juste après le gros œuvre et sont souvent confiés à la même entreprise.",
      },
      {
        type: "p",
        content:
          "Point d'attention : la mise hors d'eau (couverture posée) est un jalon majeur. Elle déclenche l'intervention des lots suivants et libère le chantier de la contrainte météo.",
      },
      { type: "h2", content: "3. Menuiseries extérieures" },
      {
        type: "p",
        content:
          "Fenêtres, portes-fenêtres, portes d'entrée, portails, volets roulants. Une fois posées, le bâtiment est dit « hors d'air ». C'est le second jalon majeur du chantier.",
      },
      {
        type: "p",
        content:
          "Anticipation critique : les menuiseries sur mesure demandent 6 à 12 semaines de fabrication. Elles doivent être commandées dès le début du chantier pour ne pas décaler la mise hors d'air.",
      },
      { type: "h2", content: "4. Plâtrerie / Isolation" },
      {
        type: "p",
        content:
          "Isolation thermique intérieure, cloisons en plaques de plâtre, doublages, faux plafonds. Ce lot conditionne les tracés des réseaux (électricité, plomberie) qui vont passer dans les cloisons.",
      },
      {
        type: "p",
        content:
          "Durée : 3 à 6 semaines pour une maison individuelle. En rénovation d'appartement, comptez 2 à 4 semaines selon la surface.",
      },
      { type: "h2", content: "5. Électricité / Courants Faibles" },
      {
        type: "p",
        content:
          "Tableau électrique, câblage, prises, interrupteurs, luminaires. Les courants faibles ajoutent les réseaux VDI, alarme, domotique.",
      },
      {
        type: "p",
        content:
          "L'électricien intervient en deux passes : le pré-câblage (avant la fermeture des cloisons) puis les finitions (une fois les peintures faites).",
      },
      { type: "h2", content: "6. Plomberie / CVC" },
      {
        type: "p",
        content:
          "Plomberie sanitaire (arrivée/évacuation d'eau), chauffage et climatisation (CVC = chauffage, ventilation, climatisation). Comme l'électricien, le plombier passe deux fois : réseaux puis pose des équipements.",
      },
      {
        type: "p",
        content:
          "Coordination clé : les plans de synthèse fluides doivent être validés avant le démarrage des cloisons, sinon les percements imprévus grèvent le budget.",
      },
      { type: "h2", content: "7. Revêtements / Peinture" },
      {
        type: "p",
        content:
          "Carrelage, parquet, revêtements muraux, peinture. Interviennent en fin de chantier, une fois tous les autres lots terminés et l'ambiance sèche stabilisée.",
      },
      {
        type: "p",
        content:
          "Les temps de séchage entre couches (24 à 72h selon le produit) doivent être respectés. Un délai comprimé génère des défauts irrattrapables.",
      },
      { type: "h2", content: "8. Aménagements extérieurs" },
      {
        type: "p",
        content:
          "VRD (voirie et réseaux divers), clôtures, terrasses, allées, plantations. Ce lot peut démarrer en parallèle du second œuvre s'il n'y a pas de conflit d'accès chantier.",
      },
      { type: "h2", content: "Ordre chronologique récapitulatif" },
      {
        type: "ul",
        items: [
          "Gros œuvre (fondations, structure)",
          "Charpente + Couverture → mise hors d'eau",
          "Menuiseries extérieures → mise hors d'air",
          "Réseaux (élec + plomberie/CVC en pré-câblage)",
          "Plâtrerie / Isolation",
          "Finitions réseaux + revêtements + peinture",
          "Aménagements extérieurs (souvent en parallèle)",
        ],
      },
      {
        type: "p",
        content:
          "Cet ordre est théorique — en pratique, de nombreuses tâches se chevauchent grâce à la coactivité. Un outil comme ChantiPlanning gère automatiquement ces chevauchements en respectant les dépendances techniques.",
      },
    ],
  },
  {
    slug: "chemin-critique-btp",
    title: "Le chemin critique en BTP : identifier et respecter les tâches clés",
    description:
      "Méthode complète pour identifier le chemin critique d'un chantier, calculer les marges des autres tâches et éviter les dérives de délais.",
    keywords:
      "chemin critique BTP, chemin critique chantier, méthode PERT, marges chantier, planning critique",
    publishedAt: "2026-02-22",
    readingMin: 7,
    heroEmoji: "⚡",
    sections: [
      {
        type: "p",
        content:
          "Le chemin critique est le concept le plus puissant du management de projet appliqué au BTP. Il identifie la séquence de tâches qui dicte la durée totale du chantier. Le maîtriser, c'est reprendre le contrôle des délais.",
      },
      { type: "h2", content: "Qu'est-ce que le chemin critique ?" },
      {
        type: "p",
        content:
          "Le chemin critique est le plus long enchaînement de tâches dépendantes dans un planning. La durée cumulée de ces tâches = la durée totale minimale du chantier. Un retard d'une seule tâche du chemin critique retarde d'autant la livraison.",
      },
      {
        type: "p",
        content:
          "À l'inverse, les tâches hors chemin critique disposent d'une marge (float). Elles peuvent glisser sans conséquence sur la date de fin, dans la limite de leur marge.",
      },
      { type: "h2", content: "Comment identifier le chemin critique" },
      {
        type: "p",
        content:
          "La méthode PERT (Program Evaluation and Review Technique) formalise le calcul. En pratique, deux passes suffisent :",
      },
      {
        type: "ul",
        items: [
          "Passe avant : pour chaque tâche, calculer sa date de début au plus tôt (ES) et sa date de fin au plus tôt (EF)",
          "Passe arrière : à partir de la fin du chantier, calculer date de début au plus tard (LS) et fin au plus tard (LF)",
          "Marge d'une tâche = LS - ES (ou LF - EF)",
          "Une tâche est critique si sa marge = 0",
        ],
      },
      {
        type: "p",
        content:
          "Sur un chantier bâtiment classique, le chemin critique passe généralement par : fondations → structure → couverture → menuiseries extérieures → plâtrerie → peintures.",
      },
      { type: "h2", content: "Piloter le chantier avec le chemin critique" },
      {
        type: "p",
        content:
          "Chaque semaine, comparez l'avancement réel des tâches critiques au prévisionnel. Trois situations possibles :",
      },
      {
        type: "ul",
        items: [
          "À l'heure ou en avance : tout va bien, continuez",
          "Retard < marge disponible : pas d'impact final, à surveiller",
          "Retard sur tâche critique : action immédiate nécessaire (renforts, heures sup, ajustement du planning aval)",
        ],
      },
      { type: "h2", content: "Les erreurs classiques" },
      {
        type: "p",
        content:
          "Ne pas surveiller les tâches à faible marge (5-10 jours). Une petite dérive successive et elles deviennent critiques. Traitez-les comme presque-critiques dès qu'il reste moins de 3 jours de marge.",
      },
      {
        type: "p",
        content:
          "Ne pas mettre à jour le planning. Un chemin critique statique perd sa valeur. Actualisez au moins une fois par semaine avec les données réelles.",
      },
      {
        type: "p",
        content:
          "Oublier les délais fournisseurs. Une menuiserie sur mesure de 8 semaines peut devenir la vraie contrainte, avant même les tâches sur site. Intégrez systématiquement les délais d'approvisionnement dans le planning.",
      },
      { type: "h2", content: "Outil : calcul automatique" },
      {
        type: "p",
        content:
          "Calculer manuellement le chemin critique sur 50 tâches prend une demi-journée. Les logiciels modernes le font en une seconde. ChantiPlanning identifie automatiquement le chemin critique et le met en évidence sur le diagramme de Gantt, avec une mise à jour dynamique dès qu'une durée change.",
      },
      { type: "h2", content: "Conclusion" },
      {
        type: "p",
        content:
          "Le chemin critique n'est pas une notion théorique. C'est le tableau de bord opérationnel du chef de chantier. Bien identifié et suivi, il divise par deux les risques de dépassement de délai. Bien communiqué aux entreprises, il aligne toute l'équipe sur les priorités réelles.",
      },
    ],
  },
];

export const findArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
